import React, { useState, useEffect } from 'react';
import { DashboardLayout } from './Dashboard';
import { useAuth, useBranding } from '../App';
import { useLanguage } from '../i18n/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Settings, User, Lock, Shield, Eye, EyeOff, CheckCircle, X,
  Globe, Smartphone, Mail, QrCode, Upload, Image, Fingerprint,
  Palette, Type, Square, RotateCcw, Save, Trash2, AlertTriangle, Loader2,
  FileText, CalendarDays, CheckSquare, Bell, PenTool, Users as UsersIcon, Database, Activity, Monitor
} from 'lucide-react';
import { toast } from 'sonner';
import { WebAuthnRegisterDialog, WebAuthnCredentialsList, useWebAuthnSupport } from '../components/WebAuthn';
import DevicePreviewTab from '../components/DevicePreviewTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Color picker component
const ColorField = ({ label, value, onChange, testId }) => (
  <div className="flex items-center justify-between gap-3">
    <Label className="text-white/70 text-sm whitespace-nowrap">{label}</Label>
    <div className="flex items-center gap-2">
      <div className="relative">
        <input
          type="color"
          value={value || '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="w-8 h-8 rounded cursor-pointer border border-white/20 bg-transparent"
          data-testid={testId}
        />
      </div>
      <Input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-24 bg-black/50 border-white/10 text-white text-xs font-mono h-8"
        placeholder="#000000"
      />
    </div>
  </div>
);

const SettingsPage = () => {
  const { user, token, updateUser } = useAuth();
  const { appName, logoUrl, loginLogoUrl, backgroundLogoUrl, backgroundLogoOpacity, backgroundLogoSize, colors, buttonStyle, fontFamily, headingFont, availableFonts, customText, updateBranding, refreshBranding } = useBranding();
  const { language, setLanguage, t } = useLanguage();
  
  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // 2FA state
  const [twoFADialogOpen, setTwoFADialogOpen] = useState(false);
  const [twoFAMethod, setTwoFAMethod] = useState('totp');
  const [twoFASetupData, setTwoFASetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [twoFALoading, setTwoFALoading] = useState(false);
  
  // WebAuthn state
  const [webAuthnDialogOpen, setWebAuthnDialogOpen] = useState(false);
  const [webAuthnRefreshKey, setWebAuthnRefreshKey] = useState(0);
  const { isSupported: webAuthnSupported, isPlatformAvailable } = useWebAuthnSupport();

  // Theme customization state
  const [themeColors, setThemeColors] = useState({});
  const [themeBtnStyle, setThemeBtnStyle] = useState('default');
  const [themeFont, setThemeFont] = useState('Inter');
  const [themeHeadingFont, setThemeHeadingFont] = useState('Space Grotesk');
  const [themeAppName, setThemeAppName] = useState('');
  const [themeCustomText, setThemeCustomText] = useState({});
  const [themeBgOpacity, setThemeBgOpacity] = useState(0.05);
  const [themeBgSize, setThemeBgSize] = useState('400px');
  const [themeSaving, setThemeSaving] = useState(false);

  // Sync branding context -> local state
  useEffect(() => {
    setThemeColors(colors || {});
    setThemeBtnStyle(buttonStyle || 'default');
    setThemeFont(fontFamily || 'Inter');
    setThemeHeadingFont(headingFont || 'Space Grotesk');
    setThemeAppName(appName || '');
    setThemeCustomText(customText || {});
    setThemeBgOpacity(backgroundLogoOpacity ?? 0.05);
    setThemeBgSize(backgroundLogoSize || '400px');
  }, [colors, buttonStyle, fontFamily, headingFont, appName, customText, backgroundLogoOpacity, backgroundLogoSize]);

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  });

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error(t('errors.passwordMismatch')); return; }
    if (newPassword.length < 8) { toast.error(t('errors.weakPassword')); return; }
    setPasswordLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST', credentials: 'include', headers: authHeaders(),
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword })
      });
      if (response.ok) {
        toast.success(t('settings.password.success'));
        setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      } else { const error = await response.json(); toast.error(error.detail || t('errors.generic')); }
    } catch { toast.error(t('errors.generic')); }
    finally { setPasswordLoading(false); }
  };

  const handleSetup2FA = async () => {
    setTwoFALoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/2fa/setup`, {
        method: 'POST', credentials: 'include', headers: authHeaders(),
        body: JSON.stringify({ method: twoFAMethod })
      });
      if (response.ok) { setTwoFASetupData(await response.json()); }
      else { const error = await response.json(); toast.error(error.detail || t('errors.generic')); }
    } catch { toast.error(t('errors.generic')); }
    finally { setTwoFALoading(false); }
  };

  const handleVerify2FA = async () => {
    if (!verificationCode || verificationCode.length !== 6) { toast.error(t('auth.twoFactor.invalidCode')); return; }
    setTwoFALoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/2fa/verify-setup`, {
        method: 'POST', credentials: 'include', headers: authHeaders(),
        body: JSON.stringify({ code: verificationCode, method: twoFAMethod })
      });
      if (response.ok) {
        toast.success('2FA enabled successfully!');
        updateUser({ two_fa_enabled: true, two_fa_method: twoFAMethod });
        setTwoFADialogOpen(false); setTwoFASetupData(null); setVerificationCode('');
      } else { const error = await response.json(); toast.error(error.detail || t('auth.twoFactor.invalidCode')); }
    } catch { toast.error(t('errors.generic')); }
    finally { setTwoFALoading(false); }
  };

  const handleDisable2FA = async () => {
    if (!window.confirm('Are you sure you want to disable 2FA?')) return;
    try {
      const response = await fetch(`${API_URL}/api/auth/2fa/disable`, {
        method: 'POST', credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (response.ok) { toast.success('2FA disabled'); updateUser({ two_fa_enabled: false, two_fa_method: null }); }
      else { const error = await response.json(); toast.error(error.detail || t('errors.generic')); }
    } catch { toast.error(t('errors.generic')); }
  };

  const handleLanguageChange = async (lang) => {
    setLanguage(lang);
    try {
      await fetch(`${API_URL}/api/auth/language`, {
        method: 'PUT', credentials: 'include', headers: authHeaders(),
        body: JSON.stringify({ language: lang })
      });
    } catch (error) { console.error('Failed to update language:', error); }
  };

  // Data Management state
  const [dataStats, setDataStats] = useState(null);
  const [clearingData, setClearingData] = useState(null);

  useEffect(() => {
    if (user?.role === 'super_admin') {
      fetch(`${API_URL}/api/admin/data-stats`, { credentials: 'include', headers: token ? { Authorization: `Bearer ${token}` } : {} })
        .then(r => r.ok ? r.json() : null).then(d => setDataStats(d)).catch(() => {});
    }
  }, [user?.role, token]);

  const handleClearData = async (dataType, label) => {
    if (!window.confirm(`Are you sure you want to permanently delete ALL ${label}? This action cannot be undone.`)) return;
    if (!window.confirm(`FINAL WARNING: This will permanently delete ALL ${label}. Type "yes" mentally and click OK to proceed.`)) return;
    setClearingData(dataType);
    try {
      const response = await fetch(`${API_URL}/api/admin/clear-data`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ data_type: dataType, confirm: true })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        // Refresh stats
        const statsRes = await fetch(`${API_URL}/api/admin/data-stats`, { credentials: 'include', headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (statsRes.ok) setDataStats(await statsRes.json());
      } else { const err = await response.json(); toast.error(err.detail || 'Failed to clear data'); }
    } catch { toast.error('Failed to clear data'); }
    setClearingData(null);
  };


  // File upload helper
  const handleFileUpload = async (file, endpoint, key) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch(`${API_URL}/api/branding/${endpoint}`, {
        method: 'POST', credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData
      });
      if (response.ok) {
        const data = await response.json();
        await refreshBranding();
        toast.success(`${key} uploaded successfully!`);
        return data;
      } else { const error = await response.json(); toast.error(error.detail || 'Upload failed'); }
    } catch { toast.error('Upload failed'); }
    return null;
  };

  // Save full theme
  const handleSaveTheme = async () => {
    setThemeSaving(true);
    try {
      const payload = {
        app_name: themeAppName,
        colors: themeColors,
        button_style: themeBtnStyle,
        font_family: themeFont,
        heading_font: themeHeadingFont,
        custom_text: themeCustomText,
        background_logo_opacity: themeBgOpacity,
        background_logo_size: themeBgSize,
      };
      const response = await fetch(`${API_URL}/api/branding`, {
        method: 'PUT', credentials: 'include', headers: authHeaders(),
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        await refreshBranding();
        toast.success('Theme saved and applied!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save theme');
      }
    } catch { toast.error('Failed to save theme'); }
    finally { setThemeSaving(false); }
  };

  const handleResetTheme = async () => {
    if (!window.confirm('Reset all theme settings to defaults?')) return;
    setThemeSaving(true);
    try {
      const payload = {
        app_name: 'SecureVault',
        colors: { primary: '#A8FF60', primary_hover: '#8FE650', secondary: '#60A8FF', background: '#050505', surface: '#0A0A0A', surface_hover: '#111111', border: '#242424', text: '#FFFFFF', text_muted: '#999999', text_subtle: '#666666', success: '#22C55E', warning: '#EAB308', error: '#EF4444', info: '#3B82F6' },
        button_style: 'default',
        font_family: 'Inter',
        heading_font: 'Space Grotesk',
        custom_text: { login_title: 'Welcome Back', login_subtitle: 'Sign in to your secure document vault', register_title: 'Create Account', register_subtitle: 'Join our secure platform', footer_text: '\u00a9 2026 SecureVault. All rights reserved.', tagline: 'Enterprise-Grade Document Security' },
        background_logo_opacity: 0.05,
        background_logo_size: '400px',
      };
      const response = await fetch(`${API_URL}/api/branding`, {
        method: 'PUT', credentials: 'include', headers: authHeaders(),
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        await refreshBranding();
        toast.success('Theme reset to defaults');
      }
    } catch { toast.error('Failed to reset theme'); }
    finally { setThemeSaving(false); }
  };

  const passwordRequirements = [
    { label: t('auth.passwordRequirements.length'), met: newPassword.length >= 8 },
    { label: t('auth.passwordRequirements.uppercase'), met: /[A-Z]/.test(newPassword) },
    { label: t('auth.passwordRequirements.number'), met: /\d/.test(newPassword) },
  ];

  const isSuperAdmin = user?.role === 'super_admin';
  const [activeSettingsTab, setActiveSettingsTab] = useState('general');

  const fontOptions = availableFonts?.length > 0 ? availableFonts : [
    'Inter', 'Space Grotesk', 'Roboto', 'Open Sans', 'Lato',
    'Poppins', 'Montserrat', 'Raleway', 'Nunito', 'Source Sans Pro',
    'Ubuntu', 'Playfair Display', 'Merriweather', 'Oswald', 'PT Sans'
  ];

  return (
    <DashboardLayout title={t('settings.title')}>
      <div className="max-w-5xl space-y-6">
        {/* Settings Tabs */}
        {isSuperAdmin && (
          <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-sm p-1 w-fit" data-testid="settings-tabs">
            <button onClick={() => setActiveSettingsTab('general')}
              className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all ${activeSettingsTab === 'general' ? 'bg-primary text-black' : 'text-white/50 hover:text-white'}`}
              data-testid="tab-general">
              <Settings className="w-4 h-4" /> General
            </button>
            <button onClick={() => setActiveSettingsTab('device-preview')}
              className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all ${activeSettingsTab === 'device-preview' ? 'bg-primary text-black' : 'text-white/50 hover:text-white'}`}
              data-testid="tab-device-preview">
              <Monitor className="w-4 h-4" /> Device Preview
            </button>
          </div>
        )}

        {/* Device Preview Tab */}
        {activeSettingsTab === 'device-preview' && isSuperAdmin ? (
          <DevicePreviewTab />
        ) : (
        <div className="max-w-3xl space-y-6">
        {/* Profile Section */}
        <div className="bg-black/40 border border-white/10 rounded-sm p-6" data-testid="settings-profile-section">
          <div className="flex items-center gap-3 mb-6">
            <User className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-white">{t('settings.profile.title')}</h2>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="relative group">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-20 h-20 rounded-full object-cover border-2 border-primary/30" />
              ) : (
                <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center text-primary text-3xl font-semibold border-2 border-primary/30">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
              )}
              <label className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                <Upload className="w-5 h-5 text-white" />
                <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > 2 * 1024 * 1024) { toast.error('Image too large. Max 2MB.'); return; }
                  const reader = new FileReader();
                  reader.onload = async () => {
                    try {
                      const res = await fetch(`${API_URL}/api/auth/profile-picture`, {
                        method: 'POST', credentials: 'include',
                        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                        body: JSON.stringify({ picture: reader.result })
                      });
                      if (res.ok) { const data = await res.json(); toast.success('Profile picture updated!'); updateUser({ picture: data.picture }); }
                      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || 'Upload failed'); }
                    } catch { toast.error('Failed to upload picture'); }
                  };
                  reader.readAsDataURL(file);
                }} data-testid="profile-picture-input" />
              </label>
              {user?.picture && (
                <button onClick={async () => {
                  try {
                    const res = await fetch(`${API_URL}/api/auth/profile-picture`, {
                      method: 'DELETE', credentials: 'include',
                      headers: token ? { Authorization: `Bearer ${token}` } : {}
                    });
                    if (res.ok) { toast.success('Picture removed'); updateUser({ picture: null }); }
                  } catch {}
                }} className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500/80 text-white flex items-center justify-center hover:bg-red-500 transition-colors" data-testid="remove-picture-btn">
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
            <div className="text-center sm:text-left">
              <p className="text-white font-medium text-lg">{user?.name}</p>
              <p className="text-white/60 text-sm">{user?.email}</p>
              <p className="text-xs font-mono uppercase tracking-wider mt-1 px-2 py-0.5 rounded-sm bg-primary/10 text-primary w-fit mx-auto sm:mx-0">
                {user?.role?.replace('_', ' ')}
              </p>
              <p className="text-white/30 text-[10px] mt-2">Hover photo to change • Max 2MB</p>
            </div>
          </div>
        </div>

        {/* Language */}
        <div className="bg-black/40 border border-white/10 rounded-sm p-6" data-testid="settings-language-section">
          <div className="flex items-center gap-3 mb-6">
            <Globe className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-white">{t('settings.language.title')}</h2>
          </div>
          <p className="text-white/60 text-sm mb-4">{t('settings.language.description')}</p>
          <Select value={language} onValueChange={handleLanguageChange}>
            <SelectTrigger className="w-full bg-black/50 border-white/10 text-white" data-testid="language-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-white/10">
              <SelectItem value="en" className="text-white">English</SelectItem>
              <SelectItem value="fr" className="text-white">Français</SelectItem>
              <SelectItem value="ht" className="text-white">Kreyòl</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* 2FA */}
        <div className="bg-black/40 border border-white/10 rounded-sm p-6" data-testid="settings-2fa-section">
          <div className="flex items-center gap-3 mb-6">
            <Smartphone className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-white">{t('settings.twoFactor.title')}</h2>
          </div>
          <p className="text-white/60 text-sm mb-4">{t('settings.twoFactor.description')}</p>
          {user?.two_fa_enabled ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-primary/10 rounded-sm border border-primary/30">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-white font-medium">{t('settings.twoFactor.enabled')}</p>
                    <p className="text-white/60 text-sm">{t('settings.twoFactor.method')}: {user.two_fa_method === 'totp' ? 'Authenticator App' : 'Email OTP'}</p>
                  </div>
                </div>
              </div>
              <Button onClick={handleDisable2FA} variant="outline" className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="disable-2fa-btn">
                {t('auth.twoFactor.disable')}
              </Button>
            </div>
          ) : (
            <Button onClick={() => setTwoFADialogOpen(true)} className="w-full bg-primary text-black hover:bg-primary/90" data-testid="enable-2fa-btn">
              {t('auth.twoFactor.enable')}
            </Button>
          )}
        </div>

        {/* WebAuthn */}
        {webAuthnSupported && (
          <div className="bg-black/40 border border-white/10 rounded-sm p-6" data-testid="settings-webauthn-section">
            <div className="flex items-center gap-3 mb-6">
              <Fingerprint className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold text-white">Biometric Authentication</h2>
            </div>
            <p className="text-white/60 text-sm mb-4">Use your fingerprint or face recognition for faster, more secure login.</p>
            <div className="space-y-4">
              <div className="border border-white/10 rounded-sm p-4">
                <h3 className="text-white text-sm font-medium mb-3">Registered Devices</h3>
                <WebAuthnCredentialsList key={webAuthnRefreshKey} token={token} onUpdate={() => setWebAuthnRefreshKey(k => k + 1)} />
              </div>
              <Button onClick={() => setWebAuthnDialogOpen(true)} className="w-full bg-primary text-black hover:bg-primary/90" data-testid="add-biometric-btn">
                <Fingerprint className="w-4 h-4 mr-2" /> Add Biometric Credential
              </Button>
              {!isPlatformAvailable && <p className="text-yellow-400/80 text-xs text-center">Your device may not support built-in biometrics.</p>}
            </div>
          </div>
        )}

        {/* Security Status */}
        <div className="bg-black/40 border border-white/10 rounded-sm p-6" data-testid="settings-security-section">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-white">{t('settings.security.title')}</h2>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-white/5 rounded-sm">
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-white font-medium">{t('settings.security.encryption')}</p>
                  <p className="text-white/60 text-sm">{t('settings.security.encryptionDesc')}</p>
                </div>
              </div>
              <span className="text-primary text-xs font-mono uppercase">{t('settings.security.active')}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-white/5 rounded-sm">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-white font-medium">{t('settings.security.accountProtection')}</p>
                  <p className="text-white/60 text-sm">{t('settings.security.accountProtectionDesc')} {user?.password_hash ? 'password' : 'Google OAuth'}</p>
                </div>
              </div>
              <span className="text-primary text-xs font-mono uppercase">{t('settings.security.secure')}</span>
            </div>
          </div>
        </div>

        {/* Password */}
        <div className="bg-black/40 border border-white/10 rounded-sm p-6" data-testid="settings-password-section">
          <div className="flex items-center gap-3 mb-6">
            <Lock className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-white">{t('settings.password.title')}</h2>
          </div>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword" className="text-white/80">{t('settings.password.current')}</Label>
              <div className="relative">
                <Input id="currentPassword" type={showPasswords ? 'text' : 'password'} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="bg-black/50 border-white/10 text-white placeholder:text-white/30 pr-10" placeholder="........" required data-testid="current-password-input" />
                <button type="button" onClick={() => setShowPasswords(!showPasswords)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/50">
                  {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword" className="text-white/80">{t('settings.password.new')}</Label>
              <Input id="newPassword" type={showPasswords ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="bg-black/50 border-white/10 text-white placeholder:text-white/30" placeholder="........" required data-testid="new-password-input" />
              <div className="space-y-1 pt-2">
                {passwordRequirements.map((req, index) => (
                  <div key={index} className="flex items-center gap-2 text-xs">
                    {req.met ? <CheckCircle className="w-3 h-3 text-primary" /> : <div className="w-3 h-3 rounded-full border border-white/20" />}
                    <span className={req.met ? 'text-primary' : 'text-white/40'}>{req.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-white/80">{t('settings.password.confirm')}</Label>
              <Input id="confirmPassword" type={showPasswords ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="bg-black/50 border-white/10 text-white placeholder:text-white/30" placeholder="........" required data-testid="confirm-password-input" />
            </div>
            <Button type="submit" disabled={passwordLoading || !currentPassword || !newPassword || newPassword !== confirmPassword} className="w-full bg-primary text-black hover:bg-primary/90 font-mono uppercase tracking-wider" data-testid="change-password-btn">
              {passwordLoading ? t('common.loading') : t('settings.password.button')}
            </Button>
          </form>
        </div>

        {/* ============ SUPER ADMIN: UI CUSTOMIZATION ============ */}
        {isSuperAdmin && (
          <>
            {/* Theme Colors */}
            <div className="bg-black/40 border border-amber-500/30 rounded-sm p-6" data-testid="theme-colors-section">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Palette className="w-5 h-5 text-amber-500" />
                  <h2 className="text-lg font-semibold text-white">Color Scheme</h2>
                  <span className="text-xs font-mono uppercase px-2 py-0.5 rounded-sm bg-amber-500/10 text-amber-500">Super Admin</span>
                </div>
              </div>
              <p className="text-white/50 text-sm mb-5">Customize the application's color palette. Changes apply globally.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ColorField label="Primary" value={themeColors.primary} onChange={(v) => setThemeColors(p => ({...p, primary: v}))} testId="color-primary" />
                <ColorField label="Primary Hover" value={themeColors.primary_hover} onChange={(v) => setThemeColors(p => ({...p, primary_hover: v}))} testId="color-primary-hover" />
                <ColorField label="Secondary" value={themeColors.secondary} onChange={(v) => setThemeColors(p => ({...p, secondary: v}))} testId="color-secondary" />
                <ColorField label="Background" value={themeColors.background} onChange={(v) => setThemeColors(p => ({...p, background: v}))} testId="color-background" />
                <ColorField label="Surface" value={themeColors.surface} onChange={(v) => setThemeColors(p => ({...p, surface: v}))} testId="color-surface" />
                <ColorField label="Border" value={themeColors.border} onChange={(v) => setThemeColors(p => ({...p, border: v}))} testId="color-border" />
                <ColorField label="Text" value={themeColors.text} onChange={(v) => setThemeColors(p => ({...p, text: v}))} testId="color-text" />
                <ColorField label="Text Muted" value={themeColors.text_muted} onChange={(v) => setThemeColors(p => ({...p, text_muted: v}))} testId="color-text-muted" />
                <ColorField label="Success" value={themeColors.success} onChange={(v) => setThemeColors(p => ({...p, success: v}))} testId="color-success" />
                <ColorField label="Warning" value={themeColors.warning} onChange={(v) => setThemeColors(p => ({...p, warning: v}))} testId="color-warning" />
                <ColorField label="Error" value={themeColors.error} onChange={(v) => setThemeColors(p => ({...p, error: v}))} testId="color-error" />
                <ColorField label="Info" value={themeColors.info} onChange={(v) => setThemeColors(p => ({...p, info: v}))} testId="color-info" />
              </div>
            </div>

            {/* Typography */}
            <div className="bg-black/40 border border-amber-500/30 rounded-sm p-6" data-testid="theme-typography-section">
              <div className="flex items-center gap-3 mb-6">
                <Type className="w-5 h-5 text-amber-500" />
                <h2 className="text-lg font-semibold text-white">Typography</h2>
                <span className="text-xs font-mono uppercase px-2 py-0.5 rounded-sm bg-amber-500/10 text-amber-500">Super Admin</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white/70 text-sm">Body Font</Label>
                  <Select value={themeFont} onValueChange={setThemeFont}>
                    <SelectTrigger className="bg-black/50 border-white/10 text-white" data-testid="font-body-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-60">
                      {fontOptions.map(f => (
                        <SelectItem key={f} value={f} className="text-white" style={{ fontFamily: `'${f}', sans-serif` }}>{f}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-white/30 text-xs" style={{ fontFamily: `'${themeFont}', sans-serif` }}>Preview: The quick brown fox jumps over the lazy dog</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70 text-sm">Heading Font</Label>
                  <Select value={themeHeadingFont} onValueChange={setThemeHeadingFont}>
                    <SelectTrigger className="bg-black/50 border-white/10 text-white" data-testid="font-heading-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-60">
                      {fontOptions.map(f => (
                        <SelectItem key={f} value={f} className="text-white" style={{ fontFamily: `'${f}', sans-serif` }}>{f}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-white/30 text-xs font-semibold" style={{ fontFamily: `'${themeHeadingFont}', sans-serif` }}>Preview: SecureVault Dashboard</p>
                </div>
              </div>
            </div>

            {/* Button Style */}
            <div className="bg-black/40 border border-amber-500/30 rounded-sm p-6" data-testid="theme-button-section">
              <div className="flex items-center gap-3 mb-6">
                <Square className="w-5 h-5 text-amber-500" />
                <h2 className="text-lg font-semibold text-white">Button Style</h2>
                <span className="text-xs font-mono uppercase px-2 py-0.5 rounded-sm bg-amber-500/10 text-amber-500">Super Admin</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { id: 'default', label: 'Default', radius: '4px' },
                  { id: 'rounded', label: 'Rounded', radius: '8px' },
                  { id: 'pill', label: 'Pill', radius: '9999px' },
                  { id: 'square', label: 'Square', radius: '0' },
                ].map(style => (
                  <button
                    key={style.id}
                    onClick={() => setThemeBtnStyle(style.id)}
                    className={`p-3 border transition-all ${themeBtnStyle === style.id ? 'border-amber-500 bg-amber-500/10' : 'border-white/10 hover:border-white/20'}`}
                    style={{ borderRadius: style.radius }}
                    data-testid={`btn-style-${style.id}`}
                  >
                    <div className="text-center">
                      <div className="inline-block px-4 py-1.5 text-xs font-medium bg-primary text-black" style={{ borderRadius: style.radius }}>
                        Button
                      </div>
                      <p className={`text-xs mt-2 ${themeBtnStyle === style.id ? 'text-amber-500' : 'text-white/50'}`}>{style.label}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Logos & Branding */}
            <div className="bg-black/40 border border-amber-500/30 rounded-sm p-6" data-testid="theme-logos-section">
              <div className="flex items-center gap-3 mb-6">
                <Image className="w-5 h-5 text-amber-500" />
                <h2 className="text-lg font-semibold text-white">Logos & Branding</h2>
                <span className="text-xs font-mono uppercase px-2 py-0.5 rounded-sm bg-amber-500/10 text-amber-500">Super Admin</span>
              </div>

              {/* App Name */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-white/70 text-sm">Application Name</Label>
                  <Input value={themeAppName} onChange={(e) => setThemeAppName(e.target.value)} className="bg-black/50 border-white/10 text-white" placeholder="SecureVault" data-testid="app-name-input" />
                </div>

                {/* Main Logo */}
                <div className="space-y-2">
                  <Label className="text-white/70 text-sm">Sidebar / Nav Logo</Label>
                  {logoUrl && <img src={logoUrl} alt="Current Logo" className="h-10 object-contain mb-2" />}
                  <input type="file" accept="image/*" id="logo-upload" className="hidden" data-testid="logo-upload-input" onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], 'logo', 'Logo')} />
                  <label htmlFor="logo-upload">
                    <Button asChild variant="outline" className="border-white/10 text-white hover:bg-white/5 cursor-pointer">
                      <span><Upload className="w-4 h-4 mr-2" /> Upload Nav Logo</span>
                    </Button>
                  </label>
                </div>

                {/* Login Logo */}
                <div className="space-y-2">
                  <Label className="text-white/70 text-sm">Login Page Logo</Label>
                  <p className="text-white/30 text-xs">Displayed on the login page hero section</p>
                  {loginLogoUrl && <img src={loginLogoUrl} alt="Login Logo" className="h-10 object-contain mb-2" />}
                  <input type="file" accept="image/*" id="login-logo-upload" className="hidden" data-testid="login-logo-upload-input" onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], 'login-logo', 'Login logo')} />
                  <label htmlFor="login-logo-upload">
                    <Button asChild variant="outline" className="border-white/10 text-white hover:bg-white/5 cursor-pointer">
                      <span><Upload className="w-4 h-4 mr-2" /> Upload Login Logo</span>
                    </Button>
                  </label>
                </div>

                {/* Background Logo */}
                <div className="space-y-2">
                  <Label className="text-white/70 text-sm">Dashboard Background Watermark</Label>
                  <p className="text-white/30 text-xs">Appears as a subtle watermark behind dashboard content</p>
                  {backgroundLogoUrl && <img src={backgroundLogoUrl} alt="Background Logo" className="h-10 object-contain mb-2" />}
                  <input type="file" accept="image/*" id="bg-logo-upload" className="hidden" data-testid="bg-logo-upload-input" onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], 'background-logo', 'Background logo')} />
                  <label htmlFor="bg-logo-upload">
                    <Button asChild variant="outline" className="border-white/10 text-white hover:bg-white/5 cursor-pointer">
                      <span><Upload className="w-4 h-4 mr-2" /> Upload Watermark</span>
                    </Button>
                  </label>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div className="space-y-2">
                      <Label className="text-white/50 text-xs">Opacity ({Math.round(themeBgOpacity * 100)}%)</Label>
                      <input type="range" min="0.01" max="0.3" step="0.01" value={themeBgOpacity} onChange={(e) => setThemeBgOpacity(parseFloat(e.target.value))} className="w-full accent-primary" data-testid="bg-opacity-slider" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/50 text-xs">Size</Label>
                      <Select value={themeBgSize} onValueChange={setThemeBgSize}>
                        <SelectTrigger className="bg-black/50 border-white/10 text-white h-8 text-xs" data-testid="bg-size-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0A0A] border-white/10">
                          <SelectItem value="200px" className="text-white text-xs">Small (200px)</SelectItem>
                          <SelectItem value="400px" className="text-white text-xs">Medium (400px)</SelectItem>
                          <SelectItem value="600px" className="text-white text-xs">Large (600px)</SelectItem>
                          <SelectItem value="800px" className="text-white text-xs">Extra Large (800px)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Custom Text */}
            <div className="bg-black/40 border border-amber-500/30 rounded-sm p-6" data-testid="theme-custom-text-section">
              <div className="flex items-center gap-3 mb-6">
                <Type className="w-5 h-5 text-amber-500" />
                <h2 className="text-lg font-semibold text-white">Custom Text Content</h2>
                <span className="text-xs font-mono uppercase px-2 py-0.5 rounded-sm bg-amber-500/10 text-amber-500">Super Admin</span>
              </div>
              <div className="space-y-4">
                {[
                  { key: 'login_title', label: 'Login Page Title', placeholder: 'Welcome Back' },
                  { key: 'login_subtitle', label: 'Login Page Subtitle', placeholder: 'Sign in to your secure document vault' },
                  { key: 'register_title', label: 'Register Page Title', placeholder: 'Create Account' },
                  { key: 'register_subtitle', label: 'Register Page Subtitle', placeholder: 'Join our secure platform' },
                  { key: 'tagline', label: 'Tagline', placeholder: 'Enterprise-Grade Document Security' },
                  { key: 'footer_text', label: 'Footer Text', placeholder: '\u00a9 2026 SecureVault. All rights reserved.' },
                ].map(field => (
                  <div key={field.key} className="space-y-1">
                    <Label className="text-white/70 text-sm">{field.label}</Label>
                    <Input
                      value={themeCustomText[field.key] || ''}
                      onChange={(e) => setThemeCustomText(p => ({...p, [field.key]: e.target.value}))}
                      className="bg-black/50 border-white/10 text-white placeholder:text-white/20"
                      placeholder={field.placeholder}
                      data-testid={`custom-text-${field.key}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Save / Reset Actions */}
            <div className="flex gap-3 sticky bottom-4 z-20" data-testid="theme-actions">
              <Button onClick={handleSaveTheme} disabled={themeSaving} className="flex-1 bg-amber-500 text-black hover:bg-amber-400 font-semibold" data-testid="save-theme-btn">
                <Save className="w-4 h-4 mr-2" /> {themeSaving ? 'Saving...' : 'Save & Apply Theme'}
              </Button>
              <Button onClick={handleResetTheme} disabled={themeSaving} variant="outline" className="border-white/10 text-white hover:bg-white/5" data-testid="reset-theme-btn">
                <RotateCcw className="w-4 h-4 mr-2" /> Reset
              </Button>
            </div>
          </>
        )}

        {/* Super Admin: Data Management */}
        {user?.role === 'super_admin' && (
          <div className="bg-black/40 border border-red-500/30 rounded-sm p-6" data-testid="data-management-section">
            <div className="flex items-center gap-3 mb-4">
              <Database className="w-5 h-5 text-red-400" />
              <h3 className="text-white font-semibold text-lg">Data Management</h3>
              <span className="text-[10px] bg-red-500/20 text-red-400 px-2 py-0.5 rounded-sm font-medium ml-auto">SUPER ADMIN ONLY</span>
            </div>
            <p className="text-white/40 text-xs mb-4">Permanently delete system data. These actions cannot be undone.</p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { type: 'audit_logs', label: 'Audit Logs', icon: Activity, count: dataStats?.audit_logs, desc: 'Clear all audit trail records' },
                { type: 'notifications', label: 'Notifications', icon: Bell, count: dataStats?.notifications, desc: 'Clear all user notifications' },
                { type: 'meetings', label: 'Meetings', icon: CalendarDays, count: dataStats?.meetings, desc: 'Clear all calendar meetings' },
                { type: 'tasks', label: 'Tasks', icon: CheckSquare, count: dataStats?.tasks, desc: 'Clear all task records' },
                { type: 'signatures', label: 'Signatures & Templates', icon: PenTool, count: dataStats?.signatures, desc: 'Clear all signatures and field templates' },
                { type: 'all_documents', label: 'All Documents', icon: FileText, count: dataStats?.documents, desc: 'Clear all documents, folders, and related data' },
                { type: 'inactive_users', label: 'Inactive/Pending Users', icon: UsersIcon, count: dataStats?.pending_users, desc: 'Remove pending, rejected, and inactive accounts' },
              ].map(item => {
                const Icon = item.icon;
                const isClearing = clearingData === item.type;
                return (
                  <div key={item.type} className="bg-black/30 border border-white/5 rounded-sm p-3 flex items-center gap-3">
                    <div className="p-2 bg-red-500/10 rounded-sm">
                      <Icon className="w-4 h-4 text-red-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium">{item.label}</p>
                      <p className="text-white/30 text-[10px]">{item.desc}</p>
                      {item.count != null && (
                        <p className="text-white/50 text-xs font-mono">{item.count} records</p>
                      )}
                    </div>
                    <Button
                      onClick={() => handleClearData(item.type, item.label)}
                      disabled={isClearing || item.count === 0}
                      size="sm"
                      className="h-8 bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/20 text-xs flex-shrink-0"
                      data-testid={`clear-${item.type}-btn`}
                    >
                      {isClearing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
                      {isClearing ? 'Clearing...' : 'Clear'}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Encryption Info */}
        <div className="bg-primary/10 border border-primary/30 rounded-sm p-6">
          <div className="flex items-start gap-3">
            <Shield className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-white font-semibold mb-2">{t('settings.encryptionInfo.title')}</h3>
              <p className="text-white/70 text-sm leading-relaxed">{t('settings.encryptionInfo.description')}</p>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* 2FA Dialog */}
      <Dialog open={twoFADialogOpen} onOpenChange={setTwoFADialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">{t('auth.twoFactor.title')}</DialogTitle>
            <DialogDescription className="text-white/60">{t('settings.twoFactor.description')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {!twoFASetupData ? (
              <>
                <div className="space-y-3">
                  <Label className="text-white/80">{t('settings.twoFactor.method')}</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button onClick={() => setTwoFAMethod('totp')} className={`p-4 rounded-sm border transition-all ${twoFAMethod === 'totp' ? 'border-primary bg-primary/10' : 'border-white/10 hover:border-white/20'}`}>
                      <QrCode className={`w-6 h-6 mx-auto mb-2 ${twoFAMethod === 'totp' ? 'text-primary' : 'text-white/60'}`} />
                      <p className={`text-sm ${twoFAMethod === 'totp' ? 'text-primary' : 'text-white/60'}`}>Authenticator</p>
                    </button>
                    <button onClick={() => setTwoFAMethod('email')} className={`p-4 rounded-sm border transition-all ${twoFAMethod === 'email' ? 'border-primary bg-primary/10' : 'border-white/10 hover:border-white/20'}`}>
                      <Mail className={`w-6 h-6 mx-auto mb-2 ${twoFAMethod === 'email' ? 'text-primary' : 'text-white/60'}`} />
                      <p className={`text-sm ${twoFAMethod === 'email' ? 'text-primary' : 'text-white/60'}`}>Email OTP</p>
                    </button>
                  </div>
                </div>
                <Button onClick={handleSetup2FA} disabled={twoFALoading} className="w-full bg-primary text-black hover:bg-primary/90" data-testid="setup-2fa-btn">
                  {twoFALoading ? t('common.loading') : t('common.next')}
                </Button>
              </>
            ) : (
              <>
                {twoFAMethod === 'totp' && twoFASetupData.qr_code && (
                  <div className="space-y-4">
                    <p className="text-white/60 text-sm">{t('auth.twoFactor.scanQR')}</p>
                    <div className="flex justify-center"><img src={twoFASetupData.qr_code} alt="QR Code" className="w-48 h-48" /></div>
                    <p className="text-xs text-white/40 text-center font-mono break-all">Secret: {twoFASetupData.secret}</p>
                  </div>
                )}
                {twoFAMethod === 'email' && <p className="text-white/60 text-sm">{t('auth.twoFactor.emailOtpSent')}</p>}
                <div className="space-y-2">
                  <Label className="text-white/80">{t('auth.twoFactor.enterCode')}</Label>
                  <Input value={verificationCode} onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))} className="bg-black/50 border-white/10 text-white text-center text-2xl tracking-widest font-mono" placeholder="000000" maxLength={6} data-testid="2fa-code-input" />
                </div>
                <Button onClick={handleVerify2FA} disabled={twoFALoading || verificationCode.length !== 6} className="w-full bg-primary text-black hover:bg-primary/90" data-testid="verify-2fa-btn">
                  {twoFALoading ? t('common.loading') : t('common.confirm')}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* WebAuthn Dialog */}
      <WebAuthnRegisterDialog open={webAuthnDialogOpen} onOpenChange={setWebAuthnDialogOpen} token={token} onSuccess={() => setWebAuthnRefreshKey(k => k + 1)} />
      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;
